<template>
  <!--
    Layout contract (single source of truth: app.css `.layout_content*` rules):

    - < 768 (phones)                      → full-screen main, rails not rendered
    - 768–1023 + touch (portrait tablets) → full-screen main, rails not rendered
    - 768–1023 no touch (narrow desktop)  → main 568px centered, rails not rendered
    - >= 1024 (large / landscape tablets) → main 568px fixed + rails rendered
      when their slot has content (flexible 180–240/180–300 so everything
      fits from 1024px up); missing rails widen the remaining track.

    `full` always forces the single-column main, whatever the viewport.
  -->
  <div
    class="layout_content"
    :class="{
      full: fullScreen,
      'layout--tablet-full': tabletFullTouch,
      'has-left': showLeft,
      'has-right': showRight,
    }"
  >
    <div v-if="showLeft" class="left_layout">
      <div class="sticky with_content">
        <slot name="left" />
      </div>
    </div>
    <div class="main_layout">
      <slot name="main" />
    </div>
    <div v-if="showRight" class="right_layout">
      <div class="sticky with_content">
        <slot name="right" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, useSlots } from "vue";
import {
  useLargeScreen,
  useSmallScreen,
  useTouchSupport,
} from "@/js/composables/useMediaQuery";

const slots = useSlots();
const hasLeft = computed(() => !!slots.left);
const hasRight = computed(() => !!slots.right);

// Rails only from the large step (1024) up — landscape tablets (>=1024 wide)
// and desktops. Everything narrower uses the full-screen main.
const { matches: isLargeScreen } = useLargeScreen();

// Touch devices below 1024 (phones + portrait tablets) get a true full-bleed
// main (no 568px cap), while narrow desktop windows keep the 568px column.
const { matches: isSmallScreen } = useSmallScreen();
const { hasTouch } = useTouchSupport();
const tabletFullTouch = computed(() => hasTouch.value && !isLargeScreen.value);

// "full" opt-out: single column on phones (and any touch device < 1024).
const fullScreen = computed(() => isSmallScreen.value || tabletFullTouch.value);

const showLeft = computed(() => hasLeft.value && isLargeScreen.value);
const showRight = computed(() => hasRight.value && isLargeScreen.value);
</script>
